/**
 * SEO rečnik za statične landing stranice — one koje nisu ni lista salona ni
 * profil, nego pričaju o samoj platformi.
 *
 * Odvojen od `seoTaxonomy.js` jer se ne tiče kategorija i gradova. Isti
 * princip: jedini izvor istine za naslove, opise i H1, koji dele i aplikacija
 * i build-time prerender, da stranica ne bi menjala naslov posle hidracije.
 *
 * Običan ESM `.js` da bi ga Node prerender mogao uvesti; tipovi u `.d.ts`.
 */

import { SITE_URL } from "./seoTaxonomy.js";

/* ── Cenovnik ────────────────────────────────────────────────────── */

/**
 * Jedini izvor cena.
 *
 * Iznos je ranije stajao na pet mesta u tri formata — u karticama na `/cene`,
 * u `Offer` šemi na srpskom i engleskom, u opisu stranice i u prerenderu.
 * Promena cene je značila pet izmena, od kojih se lako promaši jedna.
 */
export const PLANS = [
  {
    key: "solo",
    name: "Solo",
    price: 1499,
    description:
      "Plan za pojedince — neograničeno usluga i termina, podsetnici za klijente.",
  },
  {
    key: "salon",
    name: "Salon",
    price: 2499,
    description:
      "Plan za salone sa 2+ radnika — pregled zarade, kuponi, sopstveni brend.",
  },
  {
    key: "lanac",
    name: "Lanac",
    /** Cena na upit — nema `Offer` unos u strukturiranim podacima. */
    price: null,
    description:
      "Plan za više lokacija — SMS, izvoz podataka, API i prioritetna podrška.",
  },
];

/** "1499" → "1.499", kako se piše u srpskom tekstu i na karticama. */
export const formatPrice = (value) =>
  String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export const planPrice = (key) =>
  PLANS.find((p) => p.key === key)?.price ?? null;

const priceLine = PLANS.filter((p) => p.price !== null)
  .map((p) => `${p.name} ${formatPrice(p.price)} RSD`)
  .join(", ");

/**
 * `SoftwareApplication` sa cenovnikom.
 *
 * Jedna definicija za aplikaciju i prerender: ranije je prerender upisivao
 * svoju, osiromašenu verziju sa samo jednim planom, pa je `/cene` u sirovom
 * HTML-u imala dve različite `SoftwareApplication` šeme za isti proizvod.
 */
export const softwareApplicationSchema = (baseUrl = SITE_URL, overrides = {}) => {
  // Cene i nazivi planova su isti na svim jezicima; menja se samo tekst, pa
  // engleska verzija prosleđuje svoje opise umesto da prepisuje cenovnik.
  const {
    description = "Aplikacija za online zakazivanje termina i vođenje biznisa za salone, frizere, kozmetičare, berbernice, nail i makeup artiste, lične trenere i druge biznise.",
    planDescriptions = {},
    inLanguage = "sr-RS",
  } = overrides;

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "RasporediMe — Aplikacija za online zakazivanje",
    image: [`${baseUrl}/og-banner.png`],
    description,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    inLanguage,
    brand: { "@type": "Brand", name: "RasporediMe" },
    offers: PLANS.filter((plan) => plan.price !== null).map((plan) => ({
      "@type": "Offer",
      name: plan.name,
      price: String(plan.price),
      priceCurrency: "RSD",
      priceValidUntil: "2026-12-31",
      availability: "https://schema.org/InStock",
      url: `${baseUrl}/cene`,
      description: planDescriptions[plan.key] ?? plan.description,
    })),
  };
};

/* ── Stranice ────────────────────────────────────────────────────── */

/**
 * `audience` razdvaja stranice o proizvodu (vlasnik salona) od stranica za
 * posetioce koji traže termin. Nije kozmetika: `/kako-funkcionise` objašnjava
 * da je zakazivanje **besplatno za klijente**, pa bi `SoftwareApplication` sa
 * cenom od 1.499 RSD na njoj bila neistinita strukturirana informacija.
 */
export const LANDING_PAGES = {
  pricing: {
    key: "pricing",
    path: "/cene",
    audience: "business",
    linkText: "Cene",
    h1: "Cene aplikacije za zakazivanje termina",
    title: "Cene aplikacije za zakazivanje | Softver za salon — RasporediMe",
    description: `Cene RasporediMe planova: ${priceLine} mesečno. Aplikacija i softver za salon — online zakazivanje termina, knjiga termina, podsetnici. Bez ugovora, 14 dana besplatno.`,
    keywords:
      "cene aplikacije za zakazivanje, koliko košta aplikacija za zakazivanje, cena softvera za salon, softver za salon cena, program za salon cena, cena aplikacije za frizerski salon, pretplata na aplikaciju za zakazivanje, rasporedime cena",
    intro: `RasporediMe je domaći softver za salon i program za vođenje biznisa: klijenti zakazuju online 24 sata dnevno, a vi na jednom mestu vidite knjigu termina, promet po radniku i podsetnike. ${priceLine} mesečno — bez ugovora, prvih 14 dana besplatno.`,
  },

  howItWorks: {
    key: "howItWorks",
    path: "/kako-funkcionise",
    audience: "customer",
    linkText: "Kako funkcioniše",
    h1: "Kako funkcioniše online zakazivanje termina",
    title: "Kako funkcioniše online zakazivanje termina | Rasporedi Me",
    description:
      "Kako radi online zakazivanje termina preko Rasporedi Me: izaberite uslugu, pronađite salon, zakažite termin u par klikova. Za klijente besplatno, za salone 14 dana bez naplate.",
    keywords:
      "online zakazivanje termina, kako zakazati termin online, rezervacija termina online, kako funkcioniše zakazivanje termina, aplikacija za zakazivanje termina, Rasporedi Me",
    intro:
      "Online zakazivanje termina preko Rasporedi Me ide u tri koraka: izaberete uslugu, pronađete salon i potvrdite slobodan termin. Potvrda stiže odmah, bez pozivanja i bez čekanja odgovora na poruku. Za klijente je besplatno, a saloni prvih 14 dana rade bez naplate.",
    /**
     * Ključevi iz `src/i18n/*.json`. Stoje ovde da bi ista pitanja renderovala
     * stranica i upisivao prerender — inače bi FAQPage šema opisivala pitanja
     * kojih na stranici nema.
     */
    faqKeys: ["FaqFree", "FaqCancel", "FaqAccount", "FaqCities"],
  },

  contact: {
    key: "contact",
    path: "/contact",
    audience: "business",
    linkText: "Postanite partner",
    h1: "Aplikacija za salon — postanite RasporediMe partner",
    title:
      "Aplikacija za salon i softver za vođenje biznisa | Postanite partner — RasporediMe",
    description:
      "Pridružite se RasporediMe platformi — aplikacija i softver za salon: online zakazivanje termina 24/7, knjiga termina, podsetnici i promet po radniku. Bez kreditne kartice, 14 dana besplatno.",
    keywords:
      "aplikacija za salon, softver za salon, program za salon, softver za vođenje salona, aplikacija za zakazivanje termina, sistem za rezervacije za salone, online zakazivanje za biznise, postani partner",
    intro:
      "RasporediMe je aplikacija za zakazivanje termina i softver za salon, napravljena u Srbiji. Klijenti zakazuju sami preko linka koji delite na Instagramu ili sajtu, a vi vodite salon bez sveske — knjiga termina, podsetnici, cenovnik i promet po radniku na jednom mestu.",
  },

  forHair: {
    key: "forHair",
    path: "/za-frizere",
    audience: "business",
    linkText: "Aplikacija za frizere",
    h1: "Aplikacija za frizere — neka klijenti zakazuju sami",
    title: "Aplikacija za frizere i softver za frizerski salon | RasporediMe",
    description:
      "Aplikacija za frizere i softver za frizerski salon. Online zakazivanje, knjiga termina, pregled prometa po radniku, podsetnici za klijente. Domaća, na srpskom — 14 dana besplatno.",
    keywords:
      "aplikacija za frizere, aplikacija za frizerski salon, softver za frizerski salon, program za frizerski salon, knjiga termina za frizere, online zakazivanje za frizera, sistem za zakazivanje frizer",
    intro:
      "Kompletan softver za frizerski salon: online zakazivanje, knjiga termina, podsetnici i pregled prometa po radniku. Bez sveske, bez prekida šišanja zbog telefona.",
  },

  forBeauticians: {
    key: "forBeauticians",
    path: "/za-kozmeticare",
    audience: "business",
    linkText: "Aplikacija za kozmetičare",
    h1: "Aplikacija za kozmetičare — od depilacije do laserskih tretmana, sve pod kontrolom",
    title:
      "Aplikacija za kozmetičare i softver za kozmetički salon | RasporediMe",
    description:
      "Aplikacija za kozmetičare i softver za kozmetički salon. Online zakazivanje tretmana, pakovanja i kuponi, pregled klijenata i prometa po radniku. Domaća, na srpskom — 14 dana besplatno.",
    keywords:
      "aplikacija za kozmetičare, aplikacija za kozmetički salon, softver za kozmetički salon, program za kozmetički salon, online zakazivanje za kozmetičara, knjiga termina za kozmetičara, aplikacija za beauty salon",
    intro:
      "Softver za kozmetički salon koji razume da tretmani traju različito, pakovanja se menjaju i klijenti pamte svaki detalj. Online zakazivanje, knjiga klijenata i pregled tretmana — bez sveske.",
  },

  forNail: {
    key: "forNail",
    path: "/za-nail-artiste",
    audience: "business",
    linkText: "Aplikacija za nail studio",
    h1: "Aplikacija za nail artiste — manje DM poruka, više popunjenih termina",
    title: "Aplikacija za nail studio i softver za salon za nokte | RasporediMe",
    description:
      "Aplikacija za nail studio. Online zakazivanje manikira, gel laka i gradnje gelom — bez DM-ova. Cenovnik vidljiv klijentu, podsetnici smanjuju otkazivanja. 14 dana besplatno.",
    keywords:
      "aplikacija za nail studio, aplikacija za nail artiste, aplikacija za nokte, softver za nail salon, program za salon za nokte, online zakazivanje manikira, knjiga termina za nokte, aplikacija za manikir",
    intro:
      "Softver za nail studio koji razume da klijenti pitaju za boje, traže slike radova i otkazuju u poslednji čas. Online zakazivanje manikira, gel laka, gradnje noktiju i pedikira — bez sveske i bez gomile poruka.",
  },

  forMakeup: {
    key: "forMakeup",
    path: "/za-makeup-artiste",
    audience: "business",
    linkText: "Aplikacija za makeup artiste",
    h1: "Aplikacija za makeup artiste — od svakodnevnog šminkanja do svadbenih paketa",
    title:
      "Aplikacija za makeup artiste i šminkere | Softver za zakazivanje — RasporediMe",
    description:
      "Aplikacija za makeup artiste. Online zakazivanje šminkanja, svadbeni paketi sa probom, cenovnik vidljiv klijentu. Klijenti iz drugih gradova zakazuju 24/7. 14 dana besplatno.",
    keywords:
      "aplikacija za makeup artiste, aplikacija za šminkere, softver za makeup studio, program za šminkanje, online zakazivanje šminkanja, svadbeno šminkanje aplikacija, knjiga termina za makeup",
    intro:
      "Softver za makeup artiste koji razume da klijenti dolaze iz drugih gradova, traže probu pre venčanja i da svaki paket ima različita trajanja. Online zakazivanje šminkanja sa cenovnikom u dinarima — bez beskonačnih DM razgovora.",
  },
};

export const LANDING_KEYS = Object.keys(LANDING_PAGES);

/** Sve rute koje ruter mora da poznaje — `Routes.tsx` ih čita odavde. */
export const LANDING_PATHS = LANDING_KEYS.map((k) => LANDING_PAGES[k].path);

/** Ista stranica sa apsolutnim URL-om, za canonical i JSON-LD. */
export const landingPageUrl = (key) => `${SITE_URL}${LANDING_PAGES[key].path}`;

/**
 * Linkovi ka ostalim landing stranicama, grupisani po publici.
 *
 * Do sad su te stranice visile samo na footeru i u sitemap-u — međusobno se
 * nisu povezivale, pa crawler nije imao razlog da ih čita kao jednu celinu.
 */
export const getLandingLinks = (currentKey) => {
  const others = LANDING_KEYS.filter((k) => k !== currentKey).map(
    (k) => LANDING_PAGES[k],
  );

  return {
    business: others
      .filter((p) => p.audience === "business")
      .map((p) => ({ href: p.path, text: p.linkText })),
    customer: others
      .filter((p) => p.audience === "customer")
      .map((p) => ({ href: p.path, text: p.linkText })),
  };
};
